import { ChangeDetectionStrategy, Component, Optional } from '@angular/core';
import { RelatedProductsService } from '@df/catalog-ui/related-products/related-products.service';
import { app } from '@df/core/app';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ProductViewLayoutComponent } from '../../catalog/product/view/layout/product-view-layout.component';
import { NTextComponent } from '../../common/text/text.component';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NCmsRelatedProducts {
    export interface IComponentData extends NICmsContentComponent.IData, NTextComponent.ITextContent {
        /**
         * Max items qty
         */
        limit: number;
        /**
         * Source context
         */
        context?: string;
        /**
         * Engine
         */
        engine: string;
        /**
         * Products ids (manual engine)
         */
        productsIds: number[];
        /**
         * Categories ids (category engine)
         */
        categoriesIds: number[];
    }

    export enum EEngine {
        category = 'category',
        manual = 'manual',
        recentlyViewed = 'recent'
    }

    export enum ESoruce {
        cms = 'cms',
        product = 'product',
        basket = 'basket'
    }
}

let uid = 0;
@Component({
    selector: 'cms-related-products',
    templateUrl: './cms-related-products.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<CmsRelatedProductsComponent>({
    id: 'related-products',
    name: 'Related products',
    tags: ['carousel', 'product carousel'],
    fields: [
        ...NTextComponent.getFields(false, true),
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        {
            key: 'limit',
            type: NICmsContentField.EFieldType.number,
            title: 'Max products qty',
            required: true,
            default: 12
        },
        <NICmsContentField.IChoiceField>{
            key: 'context',
            type: NICmsContentField.EFieldType.choice,
            title: 'Context',
            options: () =>
                app?.meta?.model?.type === 'product'
                    ? [
                          { key: NCmsRelatedProducts.ESoruce.cms, title: NCmsRelatedProducts.ESoruce.cms },
                          { key: NCmsRelatedProducts.ESoruce.product, title: NCmsRelatedProducts.ESoruce.product },
                          { key: NCmsRelatedProducts.ESoruce.basket, title: NCmsRelatedProducts.ESoruce.basket }
                      ]
                    : [
                          { key: NCmsRelatedProducts.ESoruce.cms, title: NCmsRelatedProducts.ESoruce.cms },
                          { key: NCmsRelatedProducts.ESoruce.basket, title: NCmsRelatedProducts.ESoruce.basket }
                      ],
            default: NCmsRelatedProducts.ESoruce.cms,
            required: true
        },

        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'engine',
            title: 'Engine',
            options: (data: NCmsRelatedProducts.IComponentData) =>
                data.context !== NCmsRelatedProducts.ESoruce.cms
                    ? Object.keys(RelatedProductsService.engines)
                          .filter(i => !['manual', 'recent'].includes(i))
                          .map(i => ({ key: i, title: i }))
                    : [
                          NCmsRelatedProducts.EEngine.category,
                          NCmsRelatedProducts.EEngine.manual,
                          NCmsRelatedProducts.EEngine.recentlyViewed
                      ].map(i => ({ key: i, title: i })),
            required: true,
            default: 'manual'
        },
        <NICmsContentField.IRelationField>{
            key: 'categoriesIds',
            type: NICmsContentField.EFieldType.relation,
            title: 'Category',
            options: {
                title: 'Category',
                type: 'category',
                multiple: true
            },
            required: true,
            canShow: (data: NCmsRelatedProducts.IComponentData) =>
                data?.engine === NCmsRelatedProducts.EEngine.category && data?.context === NCmsRelatedProducts.ESoruce.cms
        },
        <NICmsContentField.IRelationField>{
            key: 'productsIds',
            type: NICmsContentField.EFieldType.relation,
            title: 'Items',
            options: {
                title: 'Product',
                type: 'product',
                multiple: true
            },
            required: true,
            default: [-100, -101, -102, -103, -104],
            canShow: (data: NCmsRelatedProducts.IComponentData) => data?.engine === NCmsRelatedProducts.EEngine.manual
        },
        NICmsContentComponent.backgroundField
    ]
})
export class CmsRelatedProductsComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component?: NCmsRelatedProducts.IComponentData;

    readonly uid = `relatedProducts${uid++}`;

    constructor(@Optional() public productViewLayoutComponent: ProductViewLayoutComponent) {
        super();
    }

    /**
     * Retrieves related product component options
     */
    protected get options() {
        return {
            engines:
                this.component?.engine === NCmsRelatedProducts.EEngine.recentlyViewed
                    ? NCmsRelatedProducts.EEngine.recentlyViewed
                    : [
                          {
                              id: this.component?.engine,
                              options: {
                                  // manual-engine
                                  ids:
                                      this.component?.engine === NCmsRelatedProducts.EEngine.manual
                                          ? this.component?.productsIds
                                          : undefined,
                                  // category-engine
                                  categoryIds:
                                      this.component?.engine === NCmsRelatedProducts.EEngine.category &&
                                      this.component?.context === NCmsRelatedProducts.ESoruce.cms
                                          ? this.component?.categoriesIds
                                          : undefined
                              }
                          }
                      ]
        };
    }

    /**
     * Retrieves related product component source
     */
    protected get source() {
        return this.component?.context === NCmsRelatedProducts.ESoruce.product && this.productViewLayoutComponent?.product
            ? this.productViewLayoutComponent.product
            : this.component?.context === NCmsRelatedProducts.ESoruce.basket
              ? 'basket'
              : undefined;
    }
}
